import { useTheme } from '../lib/theme-provider'
import { X, Palette } from 'lucide-react'
import { useState, useEffect } from 'react'

interface ThemeSelectorModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ThemeSelectorModal({ isOpen, onClose }: ThemeSelectorModalProps) {
  const { theme, setTheme, availableThemesMeta } = useTheme()
  const [selectedThemeId, setSelectedThemeId] = useState(theme.id)

  useEffect(() => {
    if (isOpen) {
      setSelectedThemeId(theme.id)
    }
  }, [isOpen, theme.id])

  const handleThemeChange = (themeId: string) => {
    setSelectedThemeId(themeId)
    setTheme(themeId as any)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        style={{ 
          backgroundColor: theme.colors['bg-card'],
          border: `2px solid ${theme.colors['border-accent']}`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: `${theme.colors['border-primary']}40` }}>
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5" style={{ color: theme.colors['accent-gold'] }} />
            <h2 className="text-lg font-semibold" style={{ color: theme.colors['text-primary'] }}>主题设置</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: theme.colors['text-secondary'] }} />
          </button>
        </div>

        {/* Theme List */}
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {availableThemesMeta.map((t) => (
            <div
              key={t.id}
              onClick={() => handleThemeChange(t.id)}
              className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 hover:scale-[1.02]`}
              style={{
                backgroundColor: t.id === selectedThemeId ? `${theme.colors['accent-gold']}20` : theme.colors['bg-secondary'],
                borderColor: t.id === selectedThemeId ? theme.colors['accent-gold'] : `${theme.colors['border-primary']}40`,
              }}
            >
              <div className="flex items-center gap-3">
                {/* Theme Preview Circle */}
                <div 
                  className="w-12 h-12 rounded-full flex-shrink-0 border-2"
                  style={{
                    background: `linear-gradient(135deg, ${t.colors['bg-primary']}, ${t.colors['accent-gold']})`,
                    borderColor: t.id === selectedThemeId ? theme.colors['accent-gold'] : theme.colors['border-primary'],
                  }}
                />
                
                {/* Theme Info */}
                <div className="flex-1">
                  <h3 
                    className="font-semibold text-base mb-1"
                    style={{ color: theme.colors['text-primary'] }}
                  >
                    {t.name}
                  </h3>
                  <p 
                    className="text-xs"
                    style={{ color: theme.colors['text-secondary'] }}
                  >
                    {t.id === 'default' ? '当前默认主题 · 经典阿瓦隆风格' : '自定义主题'}
                  </p>
                </div>

                {/* Selected Indicator */}
                {t.id === selectedThemeId && (
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div 
          className="p-4 pt-0 text-center text-xs"
          style={{ color: theme.colors['text-muted'] }}
        >
          点击主题卡片即可实时切换 · 自动保存偏好
        </div>
      </div>
    </div>
  )
}
